import { Controller } from "@hotwired/stimulus"

// Manages the Good Faith Estimate line-item rows: add/remove rows, toggle the
// per-line count override, and live-compute the session range, line cost, and
// the GFE total from the coverage period and each line's frequency.
export default class extends Controller {
  static targets = [
    "rows", "template", "total", "coverageLow", "coverageHigh",
    "row", "service", "frequency", "summary", "override", "overrideLow", "overrideHigh"
  ]

  static SESSIONS_PER_MONTH = { weekly: 52 / 12, biweekly: 26 / 12, monthly: 1 }

  connect() {
    this.recompute()
  }

  add(event) {
    event.preventDefault()
    this.rowsTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML)
    this.recompute()
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest(".gfe-line-item")
    if (row && this.rowTargets.length > 1) {
      row.remove()
      this.recompute()
    }
  }

  toggleOverride(event) {
    event.preventDefault()
    const row = event.target.closest(".gfe-line-item")
    const override = row.querySelector(".gfe-line-item-override")
    const hiding = !override.classList.contains("hidden")
    override.classList.toggle("hidden")
    if (hiding) {
      row.querySelector("[data-gfe-line-items-target='overrideLow']").value = ""
      row.querySelector("[data-gfe-line-items-target='overrideHigh']").value = ""
    }
    this.recompute()
  }

  recompute() {
    const monthsLow = this.clampedMonths(this.coverageLowTarget)
    const monthsHigh = this.clampedMonths(this.coverageHighTarget)
    let totalLow = 0
    let totalHigh = 0

    this.rowTargets.forEach((row) => {
      const rate = this.rateFor(row)
      const [low, high] = this.sessionsFor(row, monthsLow, monthsHigh)
      const lineLow = low * rate
      const lineHigh = high * rate
      totalLow += lineLow
      totalHigh += lineHigh
      const summary = row.querySelector("[data-gfe-line-items-target='summary']")
      if (summary) {
        summary.textContent =
          `${this.range(low, high)} sessions · ${this.money(lineLow, lineHigh)}`
      }
    })

    this.totalTarget.textContent = this.money(totalLow, totalHigh)
  }

  // Clamps a coverage-month input to the legal 1-12 range as the practitioner
  // types, writing the corrected value back into the field. An empty field is
  // left empty. 12 mirrors GoodFaithEstimate::MAX_COVERAGE_MONTHS.
  clampedMonths(input) {
    if (input.value === "") return 0
    let n = parseInt(input.value, 10)
    if (isNaN(n)) {
      input.value = ""
      return 0
    }
    if (n > 12) n = 12
    if (n < 1) n = 1
    if (String(n) !== input.value) input.value = String(n)
    return n
  }

  rateFor(row) {
    const select = row.querySelector("[data-gfe-line-items-target='service']")
    const option = select && select.selectedOptions[0]
    return option ? parseInt(option.dataset.rateCents, 10) || 0 : 0
  }

  // Returns [low, high] effective session counts for a row.
  sessionsFor(row, monthsLow, monthsHigh) {
    const override = row.querySelector(".gfe-line-item-override")
    if (override && !override.classList.contains("hidden")) {
      const low = parseInt(row.querySelector("[data-gfe-line-items-target='overrideLow']").value, 10)
      const high = parseInt(row.querySelector("[data-gfe-line-items-target='overrideHigh']").value, 10)
      if (low > 0 && high > 0) return [low, high]
    }
    const frequency = row.querySelector("[data-gfe-line-items-target='frequency']").value
    if (frequency === "one_time") return [1, 1]
    const perMonth = this.constructor.SESSIONS_PER_MONTH[frequency] || 0
    return [Math.round(perMonth * monthsLow), Math.round(perMonth * monthsHigh)]
  }

  range(low, high) {
    return low === high ? `${low}` : `${low}–${high}`
  }

  money(low, high) {
    const fmt = (cents) =>
      `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    return low === high ? fmt(low) : `${fmt(low)} – ${fmt(high)}`
  }
}
